import AddRoundedIcon from "@mui/icons-material/AddRounded";
import DownloadIcon from "@mui/icons-material/SaveAltRounded";
import { Button, Stack } from "@mui/material";
import { useState } from "react";
import { useNavigate } from "react-router";
import { withErrorHandling } from "@entrilia/shared/api/axiosHelper";
import AuthorizedBox from "@entrilia/shared/components/AuthorizedBox";
import DataLoadingFailed from "@entrilia/shared/components/DataLoadingFailed";
import InlineLoader from "@entrilia/shared/components/inlineLoader/InlineLoader";
import ScrollableFlexContainer from "@entrilia/shared/components/ScrollableFlexContainer";
import { useNotificationContext } from "@entrilia/shared/contexts/NotificationContext";
import { logError } from "@entrilia/shared/logging";
import { downloadFileFromUrl } from "@entrilia/shared/services/downloadFile";
import { formatDateTimeShort } from "@entrilia/shared/utilities/dateUtils";
import biApi from "../../../../../api/biApi";
import type { WorkbookTemplate } from "../../../../../api/types/workbookRuns.types";
import WorkbookReportItem from "../../common/workbook-report/WorkbookReportItem";
import WorkbookReportItemSheetName from "../../common/workbook-report/WorkbookReportItemSheetName";
import WorkbookReportsListActions from "../../common/WorkbookReportsListActions";
import CustomNumberFormatsAlert from "../../common/CustomNumberFormatsAlert";
import { TEMPLATE_CUSTOM_NUMBER_FORMATS_MESSAGE } from "../../common/workbookMessages";
import NewRunDialog from "../../dialogs/new-run-dialog/NewRunDialog";
import useReportsAccessState from "../../hooks/useReportsAccessState";

interface Props {
  workbookTemplate: WorkbookTemplate;
}

const downloadTemplate = withErrorHandling(biApi.getWorkbookTemplateDownloadInfo);

const WorkbookTemplateDetailsReports = ({ workbookTemplate }: Props) => {
  const [showNewRunDialog, setShowNewRunDialog] = useState(false);
  const navigate = useNavigate();
  const { sendNotificationError } = useNotificationContext();
  const [downloading, setDownloading] = useState(false);

  const handleNewRun = () => {
    setShowNewRunDialog(true);
  };

  const handleCloseNewRunDialog = () => {
    setShowNewRunDialog(false);
  };

  const handleRunStart = (runId: string) => {
    navigate(`../run/${runId}`);
  };

  const handleDownloadTemplate = async () => {
    setDownloading(true);
    const [downloadInfo, error] = await downloadTemplate(workbookTemplate.id);
    if (error) {
      logError(error, "[WorkbookTemplateDetailsReports] downloadTemplate");
      sendNotificationError("Could not download template");
    } else {
      await downloadFileFromUrl(downloadInfo.downloadUrl);
    }
    setDownloading(false);
  };

  const { reportsAccessState, fetchReportsAccessStateError, isFetchingReportsAccessState } = useReportsAccessState(
    workbookTemplate.reportsSettings
  );

  if (fetchReportsAccessStateError) {
    return <DataLoadingFailed text="Error fetching reports access state" />;
  }

  if (reportsAccessState === undefined || isFetchingReportsAccessState) {
    return <InlineLoader />;
  }

  return (
    <Stack sx={{ px: 3, py: 2.5, flexGrow: 1 }}>
      {workbookTemplate.hasTooManyCustomNumberFormats && (
        <CustomNumberFormatsAlert text={TEMPLATE_CUSTOM_NUMBER_FORMATS_MESSAGE} />
      )}
      <WorkbookReportsListActions
        Actions={
          <>
            <AuthorizedBox permissions={["ManageReports"]}>
              <Button variant="outlined" startIcon={<AddRoundedIcon />} onClick={handleNewRun}>
                New Run
              </Button>
            </AuthorizedBox>
            <Button
              variant="contained"
              startIcon={<DownloadIcon />}
              onClick={handleDownloadTemplate}
              loading={downloading}
            >
              Download
            </Button>
          </>
        }
      />
      <ScrollableFlexContainer>
        <Stack spacing={1}>
          {workbookTemplate.reportsSettings.map((report) => {
            const reportAccessState = reportsAccessState.data.find((state) => state.reportCode === report.reportCode);
            if (!reportAccessState) {
              logError(
                `Report access state not found for report ${report.reportCode}`,
                "[WorkbookTemplateDetailsReports] getReportsAccessState"
              );
              return null;
            }
            return (
              <WorkbookReportItem
                key={report.id}
                clientCode={report.clientCode}
                isLocked={report.isLocked ?? false}
                reportAccessState={reportAccessState}
                reportName={report.caption}
                status={undefined} //Template does not have status
                lastChangedAt={report.changedAt ? formatDateTimeShort(new Date(report.changedAt)) : ""}
                workbookFilters={workbookTemplate.workbookFilters}
                reportFilters={report.filters}
                reportKind={report.reportKind}
                reportSource={report.reportSource}
                reportId={report.id}
                reportType={report.reportType}
                SheetName={<WorkbookReportItemSheetName report={report} />}
                errorMessage={undefined} //Template does not have error message
              />
            );
          })}
        </Stack>
      </ScrollableFlexContainer>
      <AuthorizedBox permissions={["ManageReports"]}>
        {showNewRunDialog && (
          <NewRunDialog onClose={handleCloseNewRunDialog} onStartRun={handleRunStart} template={workbookTemplate} />
        )}
      </AuthorizedBox>
    </Stack>
  );
};

export default WorkbookTemplateDetailsReports;
